import {centralISOFor} from './dateUtils.js';

const MONTH_NAMES = Object.freeze([
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]);

// A calendar month key, 'YYYY-MM'. Plain `string` rather than a template
// literal type: these values come out of an <input type="month"> and a saved
// filter, and parseAccountingMonth is what actually validates the shape.
export type AccountingMonth = string;

// One animal row (cattle or sheep). The four date columns are the lifecycle
// dates the month-end test reads; `groupField` is named by the caller's config,
// which is why the herd/flock column is reached through the index signature.
export type AnimalRow = {
  id?: string | number | null;
  purchase_date?: string | null;
  birth_date?: string | null;
  created_at?: string | null;
  death_date?: string | null;
  sale_date?: string | null;
  deleted_at?: string | null;
  [column: string]: unknown;
};

// One cattle_transfers / sheep_transfers row. The animal-id column and the
// from-group column are BOTH named by the caller's config, so both are reached
// through the index signature.
export type TransferRow = {
  transferred_at?: string | null;
  created_at?: string | null;
  [column: string]: unknown;
};

// What makes this helper species-agnostic: the caller supplies the column names
// and the set of groups that count as on-farm.
export type AccountingSnapshotConfig = {
  groupField: string;
  transferEntityIdField: string;
  transferFromField: string;
  activeGroups: readonly unknown[];
};

export type TransfersByAnimal = Map<string, TransferRow[]>;

function parseAccountingMonth(value: unknown): {year: number; monthIndex: number} | null {
  const match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})$/) : null;
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) return null;
  return {year, monthIndex: month - 1};
}

function padMonth(monthIndex: number): string {
  return String(monthIndex + 1).padStart(2, '0');
}

function isoDate(value: string | number | Date | null | undefined): string | null {
  if (!value) return null;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

export function currentAccountingMonth(todayMs: number = Date.now()): AccountingMonth {
  const date = new Date(todayMs);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getUTCFullYear()}-${padMonth(date.getUTCMonth())}`;
}

export function shiftAccountingMonth(month: AccountingMonth, offset: number): AccountingMonth {
  const parsed = parseAccountingMonth(month);
  if (!parsed) return '';
  const date = new Date(Date.UTC(parsed.year, parsed.monthIndex + offset, 1, 12));
  return `${date.getUTCFullYear()}-${padMonth(date.getUTCMonth())}`;
}

export function accountingSnapshotMinMonth(todayMs: number = Date.now(), pastMonths = 12): AccountingMonth {
  const current = currentAccountingMonth(todayMs);
  return current ? shiftAccountingMonth(current, -pastMonths) : '';
}

export function accountingSnapshotMaxMonth(todayMs: number = Date.now()): AccountingMonth {
  const current = currentAccountingMonth(todayMs);
  return current ? shiftAccountingMonth(current, -1) : '';
}

export function isPastAccountingSnapshotMonth(month: AccountingMonth, todayMs: number = Date.now()): boolean {
  if (!parseAccountingMonth(month)) return false;
  const maxMonth = accountingSnapshotMaxMonth(todayMs);
  return !!maxMonth && month <= maxMonth;
}

export function accountingMonthEndISO(month: AccountingMonth): string | null {
  const parsed = parseAccountingMonth(month);
  if (!parsed) return null;
  return new Date(Date.UTC(parsed.year, parsed.monthIndex + 1, 0, 12)).toISOString().slice(0, 10);
}

export function accountingSnapshotMonthEndISO(month: AccountingMonth, todayMs: number = Date.now()): string | null {
  return isPastAccountingSnapshotMonth(month, todayMs) ? accountingMonthEndISO(month) : null;
}

export function formatAccountingMonthEnd(month: AccountingMonth): string {
  const parsed = parseAccountingMonth(month);
  const endDate = accountingMonthEndISO(month);
  if (!parsed || !endDate) return '';
  return `${MONTH_NAMES[parsed.monthIndex]} ${Number(endDate.slice(8, 10))}, ${parsed.year}`;
}

function entryDateForAnimal(row: AnimalRow | null | undefined): string | null {
  return isoDate(row && (row.purchase_date || row.birth_date || row.created_at));
}

function transferDateMs(row: TransferRow | null | undefined): number | null {
  const date = row && (row.transferred_at || row.created_at);
  if (!date) return null;
  const ms = new Date(date).getTime();
  return Number.isNaN(ms) ? null : ms;
}

function groupTransfersByAnimal(
  transfers: TransferRow[] | null | undefined,
  transferEntityIdField: string,
): TransfersByAnimal {
  const byAnimal: TransfersByAnimal = new Map();
  for (const transfer of transfers || []) {
    if (!transfer || !transfer[transferEntityIdField]) continue;
    const key = String(transfer[transferEntityIdField]);
    const list: TransferRow[] = byAnimal.get(key) || [];
    list.push(transfer);
    byAnimal.set(key, list);
  }
  for (const list of byAnimal.values()) {
    list.sort((a, b) => (transferDateMs(b) || 0) - (transferDateMs(a) || 0));
  }
  return byAnimal;
}

export function animalGroupAsOfMonthEnd(
  row: AnimalRow | null | undefined,
  transfersByAnimal: TransfersByAnimal,
  config: AccountingSnapshotConfig,
  month: AccountingMonth,
): unknown {
  const endDate = accountingMonthEndISO(month);
  if (!row || !endDate) return row ? row[config.groupField] || null : null;

  const animalTransfers: TransferRow[] = transfersByAnimal.get(String(row.id)) || [];
  // `unknown`, not the inferred `{} | null`: the herd/flock column is named by
  // the caller's config, so its value is whatever that column holds, and the
  // rewind below reassigns it from the transfer's own caller-named column.
  let group: unknown = row[config.groupField] || null;

  for (const transfer of animalTransfers) {
    const transferDate = centralISOFor(transfer.transferred_at || transfer.created_at);
    if (!transferDate) continue;
    if (transferDate <= endDate) break;
    if (transfer[config.transferFromField]) group = transfer[config.transferFromField];
  }

  return group;
}

export function animalWasActiveAtMonthEnd(
  row: AnimalRow | null | undefined,
  transfersByAnimal: TransfersByAnimal,
  config: AccountingSnapshotConfig,
  month: AccountingMonth,
  todayMs: number = Date.now(),
): boolean {
  const endDate = accountingSnapshotMonthEndISO(month, todayMs);
  if (!row || !endDate) return false;

  const entryDate = entryDateForAnimal(row);
  if (entryDate && entryDate > endDate) return false;

  const deathDate = isoDate(row.death_date);
  if (deathDate && deathDate <= endDate) return false;

  const saleDate = isoDate(row.sale_date);
  if (saleDate && saleDate <= endDate) return false;

  const deletedAt = isoDate(row.deleted_at);
  if (deletedAt && deletedAt <= endDate) return false;

  const group = animalGroupAsOfMonthEnd(row, transfersByAnimal, config, month);
  return config.activeGroups.includes(group);
}

export function accountingSnapshotRows<Row extends AnimalRow>(
  rows: Row[] | null | undefined,
  transfers: TransferRow[] | null | undefined,
  config: AccountingSnapshotConfig,
  month: AccountingMonth,
  todayMs: number = Date.now(),
): Row[] {
  const endDate = accountingSnapshotMonthEndISO(month, todayMs);
  if (!endDate) return rows || [];

  const transfersByAnimal = groupTransfersByAnimal(transfers, config.transferEntityIdField);
  return (rows || [])
    .filter((row) => animalWasActiveAtMonthEnd(row, transfersByAnimal, config, month, todayMs))
    .map((row) => {
      const groupAsOf = animalGroupAsOfMonthEnd(row, transfersByAnimal, config, month);
      return {
        ...row,
        [config.groupField]: groupAsOf,
        _accountingSnapshotMonth: month,
        _accountingSnapshotEndDate: endDate,
        _accountingSnapshotOriginalGroup: row[config.groupField] || null,
      };
    });
}
